
import React from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { GoogleDriveProvider } from "@/contexts/GoogleDriveProvider";
import Home from "./pages/Home";
import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import MedicalInfo from "./pages/MedicalInfo";
import EmployerLanding from "./pages/EmployerLanding";
import Callback from "./pages/Callback";
import Source from "./pages/Source";
import DisputeResponse from "./pages/DisputeResponse";
import NotFound from "./pages/NotFound";
import SprkzHelp from "./pages/SprkzHelp";
import AccessDenied from "./pages/AccessDenied";
import { NativeFeaturesDemo } from "./components/NativeFeaturesDemo";
import { MobileInitializer } from "./components/MobileInitializer";
import { Loader2 } from "lucide-react";
import { OAuthCallbackHandler } from "./components/OAuthCallbackHandler";

const queryClient = new QueryClient();

const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated, hasAccess, isCheckingAccess } = useAuth();

  // Not authenticated - redirect to login
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Checking access - show loading
  if (isCheckingAccess || hasAccess === null) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // No access - redirect to access denied page
  if (hasAccess === false) {
    return <Navigate to="/access-denied" replace />;
  }

  // Has access - render protected content
  return <>{children}</>;
};

const AppContent = () => (
  <BrowserRouter>
    <Routes>
      <Route path="/" element={<Home />} />
      <Route path="/login" element={<Login />} />
      <Route path="/login/callback" element={<Callback />} />
      <Route path="/callback" element={<Navigate to="/login/callback" replace />} />
      <Route path="/access-denied" element={<AccessDenied />} />
      <Route path="/source" element={<Source />} />
      <Route path="/employers" element={<EmployerLanding />} />
      <Route path="/d/:disputeId" element={<DisputeResponse />} />
      <Route path="/sprkz-help" element={<SprkzHelp />} />
      <Route path="/sprkz-help/" element={<SprkzHelp />} />
      <Route path="/sprkz" element={<SprkzHelp />} />
      <Route path="/sprkz/" element={<SprkzHelp />} />
      <Route path="/mobile-demo" element={<NativeFeaturesDemo />} />
      <Route 
        path="/dashboard" 
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        } 
      />
      <Route path="/p/:profileId" element={<MedicalInfo />} />
      {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
      <Route path="*" element={<NotFound />} />
    </Routes>
  </BrowserRouter>
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <AuthProvider>
        <GoogleDriveProvider>
          <MobileInitializer>
            <OAuthCallbackHandler onAuthComplete={() => {}} />
            <AppContent />
          </MobileInitializer>
        </GoogleDriveProvider>
      </AuthProvider>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
